import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  getGuidance, uploadGuidance, updateGuidance, deleteGuidance, errMsg,
} from '../lib/api';
import { resolveMediaUrl, toEmbedUrl, isDirectVideo } from '../lib/video';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  BookMarked, Plus, Pencil, Trash2, FileText, Download, Upload, PlayCircle, Film, Link2, FileUp,
} from 'lucide-react';
import { toast } from 'sonner';

const CATEGORIES = ['General', 'Guidebooks', 'Best Practices', 'Reference Materials', 'Templates', 'Compliance'];

const fmtSize = (b) => {
  if (!b) return '';
  if (b < 1024 * 1024) return `${Math.round(b / 1024)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};

const isVideo = (d) => d.media_type === 'video' || d.media_type === 'video_link';

export default function Guidance() {
  const { isSuperAdmin } = useAuth();
  const isAdmin = isSuperAdmin();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', description: '', category: 'General' });
  const [resourceType, setResourceType] = useState('pdf'); // 'pdf' | 'video' | 'link'
  const [file, setFile] = useState(null);
  const [videoUrl, setVideoUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [playing, setPlaying] = useState(null);

  const fetchDocs = useCallback(async () => {
    try {
      const res = await getGuidance();
      setDocs(res.data || []);
    } catch {
      toast.error('Failed to load guidance library');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  const openCreate = () => {
    setEditing(null);
    setForm({ title: '', description: '', category: 'General' });
    setResourceType('pdf');
    setFile(null);
    setVideoUrl('');
    setDialogOpen(true);
  };
  const openEdit = (d) => {
    setEditing(d);
    setForm({ title: d.title || '', description: d.description || '', category: d.category || 'General' });
    setFile(null); setVideoUrl('');
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    if (!editing) {
      if (resourceType === 'link' && !videoUrl.trim()) { toast.error('Please paste a YouTube or Vimeo link'); return; }
      if (resourceType !== 'link' && !file) { toast.error('Please choose a file to upload'); return; }
    }
    setSaving(true);
    try {
      if (editing) {
        await updateGuidance(editing.id, form);
        toast.success('Resource updated');
      } else {
        const payload = { ...form };
        if (resourceType === 'link') payload.video_url = videoUrl.trim();
        else payload.file = file;
        await uploadGuidance(payload);
        toast.success('Resource added');
      }
      setDialogOpen(false);
      fetchDocs();
    } catch (e) {
      toast.error(errMsg(e, 'Failed to save resource'));
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteGuidance(deleteTarget.id);
      toast.success('Resource removed');
      setDeleteTarget(null);
      fetchDocs();
    } catch {
      toast.error('Failed to remove resource');
    }
  };

  const grouped = docs.reduce((acc, d) => {
    const c = d.category || 'General';
    (acc[c] = acc[c] || []).push(d);
    return acc;
  }, {});

  const typeButtons = [
    { key: 'pdf', label: 'Document', icon: FileUp },
    { key: 'video', label: 'Video upload', icon: Film },
    { key: 'link', label: 'Video link', icon: Link2 },
  ];

  return (
    <div className="space-y-6" data-testid="guidance-page">

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-lg bg-primary/10 flex items-center justify-center">
            <BookMarked className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold font-['Barlow_Condensed'] uppercase tracking-tight leading-none">
              Guidance Library
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Guidebooks, best practices, videos, and reference materials for your organization.
            </p>
          </div>
        </div>
        {isAdmin && (
          <Button onClick={openCreate} data-testid="add-guidance-btn" className="gap-1.5">
            <Plus className="w-4 h-4" /> Add Resource
          </Button>
        )}
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading resources…</p>
      ) : docs.length === 0 ? (
        <Card className="p-10 text-center text-muted-foreground" data-testid="guidance-empty">
          No resources yet.{isAdmin ? ' Upload your first guidebook, video, or reference material.' : ''}
        </Card>
      ) : (
        <div className="space-y-8">
          {Object.entries(grouped).map(([cat, items]) => (
            <div key={cat}>
              <div className="flex items-center gap-2 mb-3">
                <h2 className="text-base font-semibold">{cat}</h2>
                <Badge variant="secondary">{items.length}</Badge>
              </div>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {items.map((d) => {
                  const video = isVideo(d);
                  return (
                    <Card key={d.id} className="p-4 flex flex-col" data-testid={`guidance-card-${d.id}`}>
                      <div className="flex items-start gap-3">
                        <div className={`w-9 h-9 rounded-md flex items-center justify-center flex-shrink-0 ${video ? 'bg-primary/10' : 'bg-red-500/10'}`}>
                          {video ? <Film className="w-5 h-5 text-primary" /> : <FileText className="w-5 h-5 text-red-500" />}
                        </div>
                        <div className="min-w-0 flex-1">
                          <h3 className="text-sm font-semibold leading-tight truncate">{d.title}</h3>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {video ? (d.media_type === 'video_link' ? 'Video · Link' : `${fmtSize(d.size_bytes)} · Video`) : `${fmtSize(d.size_bytes)} · PDF`}
                          </p>
                        </div>
                      </div>
                      {d.description && (
                        <p className="text-sm text-muted-foreground mt-2.5 flex-1">{d.description}</p>
                      )}
                      <div className="mt-3 flex items-center gap-1.5">
                        {video ? (
                          <Button variant="outline" size="sm" className="flex-1 gap-1.5" onClick={() => setPlaying(d)}
                            data-testid={`guidance-play-${d.id}`}>
                            <PlayCircle className="w-3.5 h-3.5" /> Watch
                          </Button>
                        ) : (
                          <a href={resolveMediaUrl(d.url)} target="_blank" rel="noopener noreferrer" className="flex-1"
                            data-testid={`guidance-download-${d.id}`}>
                            <Button variant="outline" size="sm" className="w-full gap-1.5">
                              <Download className="w-3.5 h-3.5" /> Open PDF
                            </Button>
                          </a>
                        )}
                        {isAdmin && (
                          <>
                            <Button variant="outline" size="sm" onClick={() => openEdit(d)}
                              data-testid={`edit-guidance-${d.id}`}>
                              <Pencil className="w-3.5 h-3.5" />
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => setDeleteTarget(d)}
                              className="text-destructive hover:text-destructive"
                              data-testid={`delete-guidance-${d.id}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </>
                        )}
                      </div>
                    </Card>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Resource' : 'Add Resource'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {!editing && (
              <div className="space-y-1.5">
                <Label>Resource type</Label>
                <div className="grid grid-cols-3 gap-2">
                  {typeButtons.map((t) => (
                    <button key={t.key} type="button" onClick={() => setResourceType(t.key)}
                      data-testid={`guidance-type-${t.key}`}
                      className={`flex flex-col items-center gap-1 rounded-md border px-2 py-2.5 text-xs transition-colors ${resourceType === t.key ? 'border-primary bg-primary/10 text-primary' : 'border-border hover:bg-secondary'}`}>
                      <t.icon className="w-4 h-4" />
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="g-title">Title *</Label>
              <Input id="g-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="e.g. Concussion Management Guidebook" data-testid="guidance-title-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="g-cat">Category</Label>
              <select id="g-cat" value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                data-testid="guidance-category-select">
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="g-desc">Description</Label>
              <Textarea id="g-desc" value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="What this resource covers" rows={3} data-testid="guidance-desc-input" />
            </div>
            {!editing && resourceType === 'pdf' && (
              <div className="space-y-1.5">
                <Label htmlFor="g-file">PDF file *</Label>
                <Input id="g-file" type="file" accept=".pdf"
                  onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="guidance-file-input" />
              </div>
            )}
            {!editing && resourceType === 'video' && (
              <div className="space-y-1.5">
                <Label htmlFor="g-video">Video file (MP4/WebM/MOV, max 200MB) *</Label>
                <Input id="g-video" type="file" accept=".mp4,.webm,.mov,.m4v"
                  onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="guidance-video-input" />
              </div>
            )}
            {!editing && resourceType === 'link' && (
              <div className="space-y-1.5">
                <Label htmlFor="g-link">YouTube / Vimeo URL *</Label>
                <Input id="g-link" value={videoUrl} onChange={(e) => setVideoUrl(e.target.value)}
                  placeholder="https://youtube.com/watch?v=..." data-testid="guidance-link-input" />
              </div>
            )}
            {editing && (
              <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                <Upload className="w-3.5 h-3.5" /> To replace the file/video, delete and re-add.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-guidance-btn">
              {saving ? 'Saving…' : editing ? 'Save changes' : 'Add'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Video player dialog */}
      <Dialog open={!!playing} onOpenChange={(o) => !o && setPlaying(null)}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{playing?.title}</DialogTitle>
          </DialogHeader>
          {playing && (
            <div className="relative w-full bg-black aspect-video rounded-md overflow-hidden" data-testid="guidance-player">
              {isDirectVideo(playing.url) ? (
                <video src={toEmbedUrl(playing.url)} controls className="w-full h-full">
                  <track kind="captions" />
                </video>
              ) : (
                <iframe src={toEmbedUrl(playing.url)} title={playing.title} className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen />
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove resource?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove {`"${deleteTarget?.title}"`}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} data-testid="confirm-delete-guidance-btn">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
